import { useEffect, useRef, useState } from 'react';

/**
 * Chrome since version 95+ started preventing proper styling of
 * autofilled inputs. This hook will effectively prevent browsers from
 * autofilling inputs on page load, without disabling autocomplete.
 *
 * This combined with our stylesheets is enough to have it matching our design
 * goals without compromising usability
 *
 * @param id input's id
 * @param forwardedReadOnly whether the input should be readOnly
 * @returns a readOnly prop that should be passed directly into the input. If control over
 * the prop is needed, you can pass forwardedReadOnly into this hook
 */
export function useDisableAutoFillOnLoad(
  id: string,
  forwardedReadOnly = false
) {
  const initialReadOnly = useRef(forwardedReadOnly);
  const [readOnly, setReadOnly] = useState(!forwardedReadOnly);

  useEffect(() => {
    const input = document.getElementById(id) as HTMLInputElement | null;

    if (!input || input.value.length > 0) {
      setReadOnly(false);
    } else if (!initialReadOnly.current) {
      setReadOnly(true);

      const clear: { clear?: () => void } = {};

      const timer = setTimeout(() => {
        if (clear.clear) {
          clear.clear();
        }
        setReadOnly(false);
      }, 1500);

      const callback = () => {
        if (clear.clear) {
          clear.clear();
        }
        setReadOnly(false);
        input.blur();
        input.focus();
      };

      input.addEventListener('focus', callback);
      input.addEventListener('mousedown', callback);

      clear.clear = () => {
        clearTimeout(timer);
        input.removeEventListener('focus', callback);
        input.removeEventListener('mousedown', callback);
        setReadOnly(false);
      };

      return clear.clear;
    }

    // the reason I'm telling eslint to ignore the 'id' dependency
    // is because I only want this to run on page load, not when id changes
    // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
  }, []);

  return { readOnly: forwardedReadOnly || readOnly };
}
